/**
 * Single source of truth for threat-actor color + format logic. Consolidates
 * the two divergent eve maps (ThreatActorDetailBody.jsx + ThreatIntelTab.jsx)
 * into one helper. Colors are Mantine palette names.
 */

export function activityColor(level?: string): string {
  if (level === 'active') return 'red';
  if (level === 'likely-active') return 'orange';
  if (level === 'inactive') return 'gray';
  return 'blue';
}

export function motivationColor(m?: string): string {
  if (!m) return 'gray';
  const lower = m.toLowerCase();
  if (lower.includes('espionage')) return 'violet';
  if (lower.includes('financial') || lower.includes('crime')) return 'green';
  if (lower.includes('destruction') || lower.includes('attack')) return 'red';
  return 'blue';
}

export function severityColor(count: number): string {
  if (count >= 20) return 'red';
  if (count >= 10) return 'orange';
  if (count >= 5) return 'yellow';
  return 'gray';
}

export function arsenalTypeColor(sw: {
  isRansomware: boolean;
  type: string;
}): string {
  if (sw.isRansomware) return 'red';
  if (sw.type === 'malware') return 'orange';
  return 'gray';
}

export function arsenalTypeLabel(sw: {
  isRansomware: boolean;
  type: string;
}): string {
  if (sw.isRansomware) return 'Ransomware';
  if (sw.type === 'malware') return 'Malware';
  return 'Tool';
}

export function formatMotivation(m?: string): string {
  if (!m) return 'Unknown';
  return m
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const RESOURCE_LEVEL_LABELS: Record<string, string> = {
  government: 'Nation-State',
  organization: 'Organized Group',
  team: 'Team',
  individual: 'Individual',
};

export function formatResourceLevel(r?: string): string {
  if (!r) return 'Unknown';
  return RESOURCE_LEVEL_LABELS[r] ?? r.charAt(0).toUpperCase() + r.slice(1);
}

export function formatActorDate(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

export function formatCurrency(n?: number | null): string {
  if (n == null) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

export const REVENUE_BAND_LABELS: Record<string, string> = {
  'under-10M': 'Under $10M',
  '10M-50M': '$10M – $50M',
  '50M-250M': '$50M – $250M',
  'over-250M': '$250M+',
};

export function referenceDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}
